import { instancePath } from "./instance";
import type { Doc, DocMeta } from "./docs";
/**
 * One WebSocket per tab. Subscriptions follow the set of loaded projects; on
 * reconnect every loaded project pulls a delta so nothing is missed.
 */
import { useEffect } from "react";
import { queryClient } from "./api";
import { useProjectStore } from "./store";

type ServerMessage =
  | { type: "event"; workspace_id: string; project_id: string | null; entity: string; action: string; data: unknown }
  | { type: "subscribed"; workspace_ids: string[]; project_ids: string[] }
  | { type: "pong" };

let socket: WebSocket | null = null;
let retry = 0;
let wanted = { workspaces: new Set<string>(), projects: new Set<string>() };
let closedByUs = false;

function url() {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}${instancePath}/api/ws`;
}

function sendSubscribe() {
  if (socket?.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify({ type: "subscribe", workspace_ids: [...wanted.workspaces], project_ids: [...wanted.projects] }));
  }
}

function connect() {
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return;
  closedByUs = false;
  const ws = new WebSocket(url());
  socket = ws;
  ws.onopen = () => {
    const reconnecting = retry > 0;
    retry = 0;
    sendSubscribe();
    if (reconnecting) {
      void queryClient.invalidateQueries({ queryKey: ["comments"] });
      // Missed events while offline → delta refresh for every loaded project.
      const store = useProjectStore.getState();
      for (const id of Object.keys(store.byId)) void store.refreshDelta(id);
    }
  };
  ws.onmessage = (m) => {
    let msg: ServerMessage;
    try {
      msg = JSON.parse(m.data as string);
    } catch {
      return;
    }
    if (msg.type === "event") handle(msg);
  };
  ws.onclose = () => {
    socket = null;
    if (closedByUs) return;
    const delay = Math.min(30_000, 1000 * 2 ** Math.min(retry, 5));
    retry += 1;
    setTimeout(connect, delay);
  };
  ws.onerror = () => ws.close();
}

function handle(msg: Extract<ServerMessage, { type: "event" }>) {
  const store = useProjectStore.getState();
  const d = msg.data as never;
  switch (msg.entity) {
    case "issue":
      store.applyIssue(d);
      break;
    case "state":
      store.applyState(d);
      break;
    case "label":
      store.applyLabel(d);
      break;
    case "members":
      if (msg.project_id) store.applyMembers(msg.project_id, d);
      break;
    case "relation":
      store.applyRelation(d, msg.action === "deleted" ? "deleted" : "created");
      break;
    case "project":
      if (msg.action === "updated") store.applyProject(d);
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
      break;
    case "task":
      store.applyTask(d);
      break;
    case "task_dependency":
      store.applyDependency(d, msg.action === "deleted" ? "deleted" : "created");
      break;
    case "task_update":
      store.applyHealth(d, msg.action === "deleted" ? "deleted" : "created");
      break;
    case "milestone":
      store.applyMilestone(d);
      break;
    case "doc": {
      const meta = msg.data as DocMeta;
      queryClient.setQueryData<Doc>(["doc", meta.id], (old) => old ? { ...old, ...meta } : undefined);
      void queryClient.invalidateQueries({ queryKey: ["docs"] });
      if (msg.action === "restored" || msg.action === "deleted") void queryClient.invalidateQueries({ queryKey: ["doc", meta.id] });
      break;
    }
    case "notification":
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
      break;
    case "comment": {
      const comment = msg.data as { issue_id?: string; task_id?: string; doc_id?: string; author_id: string };
      for (const kind of ["issue", "task", "doc"] as const) {
        const id = comment[`${kind}_id`];
        if (id) {
          void queryClient.invalidateQueries({ queryKey: ["comments", kind, id] });
          void queryClient.invalidateQueries({ queryKey: [kind, id, "activities"] });
        }
      }
      if (msg.project_id && !store.byId[msg.project_id]?.users[comment.author_id]) void store.refreshDelta(msg.project_id);
      break;
    }
    default:
      break;
  }
}

export function setSubscriptions(workspaceIds: string[], projectIds: string[]) {
  wanted = { workspaces: new Set(workspaceIds), projects: new Set(projectIds) };
  if (!socket) connect();
  else sendSubscribe();
}

export function disconnect() {
  closedByUs = true;
  socket?.close();
  socket = null;
}

/** Keep the socket subscribed to the workspace and every loaded project. */
export function useRealtime(workspaceId: string) {
  const projectIds = useProjectStore((s) => Object.keys(s.byId).join(","));
  useEffect(() => {
    setSubscriptions([workspaceId], projectIds ? projectIds.split(",") : []);
  }, [workspaceId, projectIds]);
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        connect();
        void queryClient.invalidateQueries({ queryKey: ["comments"] });
        const store = useProjectStore.getState();
        for (const id of Object.keys(store.byId)) void store.refreshDelta(id);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);
}
